/**
 * AI CONTEXT
 * Purpose: Standout Review dashboard. Review is one card per student per
 *   class session for the last 3 months, grouped by day then class. The
 *   left column is still to decide. The right column is already decided.
 *   Stream is the semester-scoped grouping. Teacher stats stays its own
 *   mode. Group-by is disabled while Review is open. Page shell modeled
 *   on QualityCheckView, but this surface is a normal LIVE listener page
 *   (not a one-shot read): leads/managers want it to update as teachers
 *   keep logging moments.
 * Data: moments come from a semester-scoped `useFilteredDocs` listener;
 *   classes/curricula/students/sessions/projects load via `useSomeDocs` from
 *   ids derived off the moments actually in view (see the id-derivation
 *   block below for exactly which subsets are subscribed and why).
 * Referenced by: App route ROUTES.STANDOUT_REVIEW.
 */
import { ClearOutlined, SearchOutlined } from "@ant-design/icons"
import {
  App,
  Button,
  Input,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from "antd"
import { sortBy, uniq } from "lodash"
import { useCallback, useEffect, useMemo, useState } from "react"

import {
  familyDeliveryAtoms,
  standoutMomentAtoms,
  storyTimeAtoms,
} from "#atoms"
import {
  classAtoms,
  classSessionAtoms,
  curriculumAtoms,
  evaluationKeywordAtoms,
  projectAtoms,
  studentAtoms,
} from "#atoms"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import {
  type ReviewClassTeachers,
  selectClassImagesForReview,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { HEADER_HEIGHT } from "#components/Header"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import {
  type StandoutReviewFiltersSetting,
  type StandoutReviewGroupBy,
  type Uuid,
} from "#customTypes/globalTypes"
import { SemesterOptions } from "#forms/SemesterDropdown"
import { useTimeout } from "#hooks/jotai/useCascadingData"
import {
  useGetAllStudents,
  useGetSomeStudents,
} from "#hooks/jotai/useGetStudents"
import { useFilteredLoadingState } from "#hooks/jotai/useLoadingState"
import {
  useAllDocs,
  useFilteredDocs,
  useSomeDocs,
} from "#hooks/jotai/useSubscription"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { type FamilyDeliveryConstructor } from "#utils/familyDelivery"
import { formatSemester, getCurrentSemester } from "#utils/semesterHelpers"
import { sentNoteSharePatch } from "#utils/standoutMomentMigration"

import {
  historyWindowStart,
  momentShareState,
  selectHistoryScans,
  selectSessionArtwork,
} from "./reviewCardHistory"
import { teacherAppScanAuthorIds } from "./standoutReviewCardHelpers"
import {
  buildStandoutReviewOptions,
  countDistinctTeachers,
  DEFAULT_STANDOUT_REVIEW_FILTERS_SETTING,
  familyStateWrite,
  groupSessionsByClassId,
  indexById,
  momentMatchesFilters,
  momentMatchesStudentSearch,
  parseStandoutReviewFiltersSetting,
  resolveMomentProjectId,
  STANDOUT_CATEGORY_LABELS,
  STANDOUT_REVIEW_GROUP_BY_LABELS,
  type StandoutReviewJoins,
  type StandoutReviewMomentFilters,
  storyTimeMatchesTeacher,
} from "./standoutReviewHelpers"
import StandoutReviewColumns from "./StandoutReviewHistory"
import StandoutReviewSkeleton from "./StandoutReviewSkeleton"
import StandoutReviewStream from "./StandoutReviewStream"
import StandoutTeacherStats from "./StandoutTeacherStats"

const { Title, Text } = Typography

const REVIEW_MODE = {
  REVIEW: "review",
  STREAM: "stream",
  TEACHER_STATS: "teacherStats",
} as const
type ReviewMode = (typeof REVIEW_MODE)[keyof typeof REVIEW_MODE]

const STUDENTS_WAIT_MS = 3000

/** antd Select shows the placeholder for `null`, so "All" is a real sentinel option. */
const ALL_OPTION_VALUE = "__all__"
const toSelectValue = (value: string | null) => value ?? ALL_OPTION_VALUE
const fromSelectValue = (value: string) =>
  value === ALL_OPTION_VALUE ? null : value

const GROUP_BY_OPTIONS = Object.entries(STANDOUT_REVIEW_GROUP_BY_LABELS).map(
  ([value, label]) => ({ value: value as StandoutReviewGroupBy, label })
)

const hasActiveFilters = (filters: StandoutReviewFiltersSetting): boolean =>
  filters.teacherId !== null ||
  filters.classId !== null ||
  filters.category !== null ||
  filters.curriculumId !== null ||
  filters.groupBy !== "student" ||
  filters.hideFinished === false ||
  filters.hideFinishedClasses === true ||
  // A non-default semester is clearable state too (same posture as QC).
  filters.semester !== getCurrentSemester()

const StandoutReviewView = () => {
  const theme = useThemeContext()
  const { getUserSetting, setUserSetting, currentUserId } = useUserContext()
  const { message } = App.useApp()

  // --- Filters: persisted value is the render-time fallback; the local
  // override wins the instant the user interacts (same pattern as
  // QualityCheckView — see data-flow-firestore-jotai's userSettings note on
  // why a render straight off getUserSetting lags the debounced write).
  const storedFiltersRaw = getUserSetting<StandoutReviewFiltersSetting>(
    USER_SETTINGS.STANDOUT_REVIEW_FILTERS
  )
  const storedFilters = useMemo(
    () => parseStandoutReviewFiltersSetting(storedFiltersRaw),
    [storedFiltersRaw]
  )
  const [filtersOverride, setFiltersOverride] =
    useState<StandoutReviewFiltersSetting | null>(null)
  const filters = filtersOverride ?? storedFilters

  const updateFilters = useCallback(
    (patch: Partial<StandoutReviewFiltersSetting>) => {
      const next = { ...filters, ...patch }
      setFiltersOverride(next)
      setUserSetting(USER_SETTINGS.STANDOUT_REVIEW_FILTERS, next)
    },
    [filters, setUserSetting]
  )

  // Student search is local state only — search is ephemeral, unlike the
  // persisted selects. Declared here so Clear can reset it too.
  const [studentSearch, setStudentSearch] = useState("")

  const clearFilters = useCallback(() => {
    setFiltersOverride(DEFAULT_STANDOUT_REVIEW_FILTERS_SETTING)
    setStudentSearch("")
    setUserSetting(
      USER_SETTINGS.STANDOUT_REVIEW_FILTERS,
      DEFAULT_STANDOUT_REVIEW_FILTERS_SETTING
    )
  }, [setUserSetting])

  // --- Moments: a real listener, semester-scoped — this page updates live
  // as teachers keep logging (unlike Quality Check's one-shot read).
  const momentQuery = useMemo(
    () => ({
      queries: {
        semester: { value: filters.semester, comparator: COMPARATORS.EQUALS },
      },
    }),
    [filters.semester]
  )
  const moments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentQuery
  )
  // Card history is the last 3 months, which can cross a semester boundary.
  // The instant is fixed for this visit so the listener key stays put.
  const historySinceMs = useMemo(() => historyWindowStart(Date.now()), [])
  const historySince = useMemo(
    () => new Date(historySinceMs).toISOString(),
    [historySinceMs]
  )
  const historyMomentQuery = useMemo(
    () => ({
      queries: {
        createdAt: {
          value: historySince,
          comparator: COMPARATORS.GREATER_OR_EQUALS,
        },
      },
    }),
    [historySince]
  )
  const historyMoments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    historyMomentQuery
  )
  const storyTimeQuery = useMemo(
    () => ({
      queries: {
        semester: { value: filters.semester, comparator: COMPARATORS.EQUALS },
      },
    }),
    [filters.semester]
  )
  const storyTimes = useFilteredDocs<StoryTimeConstructor>(
    storyTimeAtoms,
    DOC_TYPES.STORY_TIMES,
    storyTimeQuery
  )
  // Card history crosses semesters. A Story Time from the previous semester
  // still belongs to that session's send clock.
  const historyStoryTimeQuery = useMemo(
    () => ({
      queries: {
        timeCreated: {
          value: historySinceMs,
          comparator: COMPARATORS.GREATER_OR_EQUALS,
        },
      },
    }),
    [historySinceMs]
  )
  const historyStoryTimes = useFilteredDocs<StoryTimeConstructor>(
    storyTimeAtoms,
    DOC_TYPES.STORY_TIMES,
    historyStoryTimeQuery
  )
  const deliveryQuery = useMemo(
    () => ({
      queries: {
        status: { value: "scheduled", comparator: COMPARATORS.EQUALS },
      },
    }),
    []
  )
  const scheduledDeliveries = useFilteredDocs<FamilyDeliveryConstructor>(
    familyDeliveryAtoms,
    DOC_TYPES.FAMILY_DELIVERIES,
    deliveryQuery
  )
  const notifyAtByKey = useMemo(() => {
    const map: Record<string, string> = {}
    for (const delivery of scheduledDeliveries) {
      if (
        !delivery.notifyAt ||
        !delivery.studentId ||
        !delivery.classSessionId
      ) {
        continue
      }
      map[`${delivery.studentId}__${delivery.classSessionId}`] =
        delivery.notifyAt
    }
    return map
  }, [scheduledDeliveries])

  // Every class in the selected semester (not just ones moments reference) —
  // the teacher stats table's roster coverage needs each teacher's FULL
  // semester roster, including classes with zero standout moments logged.
  const classQuery = useMemo(
    () => ({
      queries: {
        semester: { value: filters.semester, comparator: COMPARATORS.EQUALS },
      },
    }),
    [filters.semester]
  )
  const semesterClasses = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classQuery
  )

  const users = useGetAllUsers()

  const momentsLoad = useFilteredLoadingState(standoutMomentAtoms, momentQuery)
  const historyMomentsLoad = useFilteredLoadingState(
    standoutMomentAtoms,
    historyMomentQuery
  )
  const storyTimesLoad = useFilteredLoadingState(storyTimeAtoms, storyTimeQuery)
  const historyStoryTimesLoad = useFilteredLoadingState(
    storyTimeAtoms,
    historyStoryTimeQuery
  )
  const semesterClassesLoad = useFilteredLoadingState(classAtoms, classQuery)

  // UPDATE on this resource is the moderation grant: thumbs-down/share/edit-
  // others. Needs-work marks are visible only to this grant plus the
  // moment's own author (enforced per-card by the other agent's component).
  const canModerate = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )

  // --- Ids derived from the moments in view, sorted for stable subscription
  // keys (useSomeDocs dedupes/sorts internally too, but a stable array here
  // avoids recomputing it downstream on every render).
  const classIds = useMemo(
    () =>
      sortBy(
        uniq([
          ...[...moments, ...historyMoments]
            .map((moment) => moment.classId)
            .filter((id): id is Uuid => !!id),
          ...[...storyTimes, ...historyStoryTimes]
            .map((storyTime) => storyTime.classId)
            .filter((id): id is Uuid => !!id),
        ])
      ),
    [historyMoments, historyStoryTimes, moments, storyTimes]
  )
  const curriculumIds = useMemo(
    () =>
      sortBy(
        uniq(
          [...moments, ...historyMoments]
            .map((moment) => moment.curriculumId)
            .filter((id): id is Uuid => !!id)
        )
      ),
    [historyMoments, moments]
  )
  const studentIds = useMemo(
    () =>
      sortBy(
        uniq([
          ...moments.map((moment) => moment.studentId),
          ...historyMoments.map((moment) => moment.studentId),
          ...storyTimes.map((storyTime) => storyTime.studentId),
        ])
      ),
    [historyMoments, moments, storyTimes]
  )

  const classes = useSomeDocs(classAtoms, DOC_TYPES.CLASSES, classIds)
  const curricula = useSomeDocs(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    curriculumIds
  )
  const subjectStudents = useSomeDocs(
    studentAtoms,
    DOC_TYPES.STUDENTS,
    studentIds
  )
  // TAs are students. Their ids are not the moment's studentId, and the
  // base student subscription already has them — no extra listener.
  const taStudentIds = useMemo(
    () =>
      sortBy(
        uniq(
          [...moments, ...historyMoments].flatMap((moment) =>
            moment.assistantId ? [moment.assistantId] : []
          )
        )
      ),
    [historyMoments, moments]
  )
  const taStudents = useGetSomeStudents(taStudentIds)
  // Class scans live on the student doc. The base subscription already has
  // every student, so a kid with only scans (no note, no Story Time) is still
  // in this list.
  const allStudents = useGetAllStudents()
  // The app-wide student listener has no loaded flag. Names and scans fill
  // in from it, so wait for the first students (or give up after a beat).
  const studentsSettled = useTimeout(STUDENTS_WAIT_MS, allStudents.length > 0)
  // A TA who only took another kid's photo is not a moment subject and may
  // have no artwork of their own. The base list already has that student.
  const scanAuthorIds = useMemo(
    () => teacherAppScanAuthorIds(allStudents),
    [allStudents]
  )
  const students = useMemo(() => {
    const byId = indexById(subjectStudents)
    taStudents.forEach((student) => {
      if (!byId[student.id]) byId[student.id] = student
    })
    const allById = indexById(allStudents)
    for (const student of allStudents) {
      if (!byId[student.id] && student.artwork) byId[student.id] = student
    }
    for (const id of scanAuthorIds) {
      const author = allById[id]
      if (author && !byId[author.id]) byId[author.id] = author
    }
    return Object.values(byId)
  }, [allStudents, scanAuthorIds, subjectStudents, taStudents])
  // Semester classes cover scan-only and Story Time-only sessions. Moment
  // lookups still overlay, in case a note points at a class outside the
  // semester query. History also loads classes for scans from the last 3 months.
  const historyScanClassIds = useMemo(() => {
    const ids = new Set<string>()
    for (const student of allStudents) {
      for (const piece of Object.values(student.artwork ?? {})) {
        if (!piece?.classId || !piece.classSessionId) continue
        if (piece.isHidden === true) continue
        if ((piece.timeCreated ?? 0) < historySinceMs) continue
        ids.add(piece.classId)
      }
    }
    return sortBy([...ids])
  }, [allStudents, historySinceMs])
  const historyScanClasses = useSomeDocs(
    classAtoms,
    DOC_TYPES.CLASSES,
    historyScanClassIds
  )
  const classesById = useMemo(() => {
    const byId = indexById(semesterClasses)
    for (const classObj of classes) {
      byId[classObj.id] = classObj
    }
    for (const classObj of historyScanClasses) {
      byId[classObj.id] = classObj
    }
    return byId
  }, [classes, historyScanClasses, semesterClasses])

  // Sessions are only needed to INFER a project for moments that never
  // stamped one — loading sessions for every class in view would be most of
  // the semester's schedule for no reason.
  const classIdsNeedingSessions = useMemo(
    () =>
      sortBy(
        uniq(
          moments
            .filter((moment) => !moment.projectId && moment.classId)
            .map((moment) => moment.classId as Uuid)
        )
      ),
    [moments]
  )
  const sessionIds = useMemo(() => {
    const ids = classIdsNeedingSessions.flatMap(
      (classId) => classesById[classId]?.sessionIds ?? []
    )
    return sortBy(uniq(ids))
  }, [classIdsNeedingSessions, classesById])
  const sessions = useSomeDocs(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionIds
  )
  const sessionsByClassId = useMemo(
    () => groupSessionsByClassId(sessions),
    [sessions]
  )

  const projectIds = useMemo(() => {
    const ids = moments
      .map((moment) => resolveMomentProjectId(moment, sessionsByClassId))
      .filter((id): id is Uuid => !!id)
    return sortBy(uniq(ids))
  }, [moments, sessionsByClassId])
  const projects = useSomeDocs(projectAtoms, DOC_TYPES.PROJECTS, projectIds)

  // Small collection, subscribed whole — moments may reference deactivated
  // tags, and the isActive filter used elsewhere would drop those labels.
  const tags = useAllDocs(evaluationKeywordAtoms, DOC_TYPES.EVALUATION_KEYWORDS)

  const joins: StandoutReviewJoins = useMemo(
    () => ({
      classesById,
      curriculaById: indexById(curricula),
      projectsById: indexById(projects),
      studentsById: indexById(students),
      usersById: indexById(users),
      tagsById: indexById(tags),
      sessionsByClassId,
    }),
    [classesById, curricula, projects, students, users, tags, sessionsByClassId]
  )

  const momentFilters: StandoutReviewMomentFilters = useMemo(
    () => ({
      teacherId: filters.teacherId,
      classId: filters.classId,
      category: filters.category,
      curriculumId: filters.curriculumId,
    }),
    [filters.category, filters.classId, filters.curriculumId, filters.teacherId]
  )

  // The student search scopes everything downstream (facets included), like
  // Quality Check's semester filter: searching for a kid narrows every
  // option count.
  const searchedMoments = useMemo(
    () =>
      studentSearch.trim()
        ? moments.filter((moment) =>
            momentMatchesStudentSearch(
              moment,
              studentSearch,
              joins.studentsById
            )
          )
        : moments,
    [joins.studentsById, moments, studentSearch]
  )

  const filteredMoments = useMemo(
    () =>
      searchedMoments.filter((moment) =>
        momentMatchesFilters(moment, momentFilters)
      ),
    [momentFilters, searchedMoments]
  )

  const momentSessionIds = useMemo(
    () =>
      new Set(
        filteredMoments
          .map((moment) => moment.classSessionId)
          .filter((id): id is Uuid => !!id)
      ),
    [filteredMoments]
  )
  const momentFiltersOpen = filters.category === null

  const storyTimesInView = useMemo(() => {
    return storyTimes.filter((storyTime) => {
      if (filters.classId !== null && storyTime.classId !== filters.classId) {
        return false
      }
      if (
        filters.curriculumId !== null &&
        joins.classesById[storyTime.classId ?? ""]?.curriculumId !==
          filters.curriculumId
      ) {
        return false
      }
      if (
        !storyTimeMatchesTeacher(
          storyTime,
          filters.teacherId,
          joins.classesById
        )
      ) {
        return false
      }
      if (
        studentSearch.trim() &&
        !momentMatchesStudentSearch(
          storyTime,
          studentSearch,
          joins.studentsById
        )
      ) {
        return false
      }
      if (momentSessionIds.has(storyTime.classSessionId)) return true
      return momentFiltersOpen
    })
  }, [
    filters.classId,
    filters.curriculumId,
    filters.teacherId,
    joins.classesById,
    joins.studentsById,
    momentFiltersOpen,
    momentSessionIds,
    storyTimes,
    studentSearch,
  ])

  const namesByStudentId = useMemo(() => {
    const names: Record<string, string | undefined> = {}
    for (const student of allStudents) names[student.id] = student.name
    return names
  }, [allStudents])

  const reviewClassIdsNeeded = useMemo(() => {
    if (filters.curriculumId === null && filters.teacherId === null) return []
    const ids = new Set<string>(historyScanClassIds)
    for (const story of [...historyStoryTimes, ...storyTimes]) {
      if (story.classId) ids.add(story.classId)
    }
    return sortBy([...ids])
  }, [
    filters.curriculumId,
    filters.teacherId,
    historyScanClassIds,
    historyStoryTimes,
    storyTimes,
  ])
  const reviewClassesReady = reviewClassIdsNeeded.every(
    (id) => classesById[id] != null
  )
  const reviewClassesSettled = useTimeout(STUDENTS_WAIT_MS, reviewClassesReady)

  const reviewClassesById = useMemo(() => {
    const byId: Record<string, ReviewClassTeachers> = {}
    for (const classObj of semesterClasses) {
      byId[classObj.id] = {
        teacherIds: classObj.teacherIds,
        defaultTeacherId: classObj.defaultTeacherId,
        curriculumId: classObj.curriculumId,
      }
    }
    return byId
  }, [semesterClasses])

  const classImagesInView = useMemo(
    () =>
      selectClassImagesForReview(allStudents, {
        semester: filters.semester,
        classId: filters.classId,
        teacherId: filters.teacherId,
        classesById: reviewClassesById,
        studentSearch,
        namesByStudentId,
        momentSessionIds,
        momentFiltersOpen,
        curriculumId: filters.curriculumId,
      }),
    [
      filters.classId,
      filters.curriculumId,
      filters.semester,
      filters.teacherId,
      momentFiltersOpen,
      momentSessionIds,
      namesByStudentId,
      allStudents,
      reviewClassesById,
      studentSearch,
    ]
  )

  const historyFilteredMoments = useMemo(() => {
    const searched = studentSearch.trim()
      ? historyMoments.filter((moment) =>
          momentMatchesStudentSearch(moment, studentSearch, joins.studentsById)
        )
      : historyMoments
    return searched.filter((moment) =>
      momentMatchesFilters(moment, momentFilters)
    )
  }, [historyMoments, joins, momentFilters, studentSearch])
  const historyScans = useMemo(() => {
    const rows = selectHistoryScans(allStudents, {
      classId: filters.classId,
      teacherId: filters.teacherId,
      classesById: joins.classesById,
      studentSearch,
      namesByStudentId,
    }).filter((row) => {
      if (filters.curriculumId === null) return true
      const classId = row.artwork.classId
      if (!classId) return false
      return joins.classesById[classId]?.curriculumId === filters.curriculumId
    })
    if (momentFiltersOpen) return rows
    const sessions = new Set(
      historyFilteredMoments
        .map((moment) => moment.classSessionId)
        .filter((id): id is string => !!id)
    )
    return rows.filter(
      (row) =>
        !!row.artwork.classSessionId && sessions.has(row.artwork.classSessionId)
    )
  }, [
    allStudents,
    filters.classId,
    filters.curriculumId,
    filters.teacherId,
    historyFilteredMoments,
    momentFiltersOpen,
    joins.classesById,
    namesByStudentId,
    studentSearch,
  ])
  // Unshare decides from the whole session, not the filtered cards. A note,
  // Story Time, or photo can stay shared after the visible item is gone.
  const sessionMoments = useMemo(() => {
    const byId = new Map<string, StandoutMomentConstructor>()
    for (const moment of historyMoments) byId.set(moment.id, moment)
    for (const moment of moments) byId.set(moment.id, moment)
    return [...byId.values()]
  }, [historyMoments, moments])
  const sessionStoryTimes = useMemo(() => {
    const byId = new Map<string, StoryTimeConstructor>()
    for (const storyTime of storyTimes) byId.set(storyTime.id, storyTime)
    for (const storyTime of historyStoryTimes) byId.set(storyTime.id, storyTime)
    return [...byId.values()]
  }, [historyStoryTimes, storyTimes])
  const sessionArtwork = useMemo(
    () => selectSessionArtwork(allStudents),
    [allStudents]
  )

  const options = useMemo(
    () => buildStandoutReviewOptions(searchedMoments, joins, momentFilters),
    [searchedMoments, joins, momentFilters]
  )

  const teacherOptions = useMemo(
    () => [
      { value: ALL_OPTION_VALUE, label: "All teachers" },
      ...options.teachers.map((option) => ({
        value: option.value,
        label: `${option.label} (${option.count})`,
      })),
    ],
    [options.teachers]
  )
  const classOptions = useMemo(
    () => [
      { value: ALL_OPTION_VALUE, label: "All classes" },
      ...options.classes.map((option) => ({
        value: option.value,
        label: `${option.label} (${option.count})`,
      })),
    ],
    [options.classes]
  )
  const curriculumOptions = useMemo(
    () => [
      { value: ALL_OPTION_VALUE, label: "All curriculums" },
      ...options.curricula.map((option) => ({
        value: option.value,
        label: `${option.label} (${option.count})`,
      })),
    ],
    [options.curricula]
  )
  const { categoryCounts } = options
  const categoryOptions = useMemo(
    () => [
      { value: ALL_OPTION_VALUE, label: "All categories" },
      ...Object.entries(STANDOUT_CATEGORY_LABELS).map(([value, label]) => ({
        value,
        label: `${label} (${categoryCounts[value as keyof typeof categoryCounts]})`,
      })),
    ],
    [categoryCounts]
  )

  const [mode, setMode] = useState<ReviewMode>(REVIEW_MODE.REVIEW)
  // The stream is heavy. Paint the segmented control and a spinner first,
  // then mount it on the next turn. Leaving Stream clears the flag so the
  // next visit spins again.
  const [streamReady, setStreamReady] = useState(false)
  useEffect(() => {
    if (mode !== REVIEW_MODE.STREAM) {
      setStreamReady(false)
      return
    }
    const handle = setTimeout(() => {
      setStreamReady(true)
    }, 0)
    return () => {
      clearTimeout(handle)
    }
  }, [mode])

  const momentsForCounts =
    mode === REVIEW_MODE.REVIEW ? historyFilteredMoments : filteredMoments
  const shareCounts = useMemo(() => {
    let undecided = 0
    let decided = 0
    for (const moment of momentsForCounts) {
      const state = momentShareState(moment)
      if (state === "staff") continue
      if (state === "shared" || state === "blocked") decided += 1
      else undecided += 1
    }
    return { undecided, decided }
  }, [momentsForCounts])

  const loading =
    !studentsSettled ||
    (mode === REVIEW_MODE.REVIEW
      ? historyMomentsLoad.isInitialLoad ||
        historyStoryTimesLoad.isInitialLoad ||
        semesterClassesLoad.isInitialLoad ||
        !reviewClassesSettled
      : mode === REVIEW_MODE.STREAM
        ? momentsLoad.isInitialLoad || storyTimesLoad.isInitialLoad
        : momentsLoad.isInitialLoad || semesterClassesLoad.isInitialLoad)

  const handleApproveForFamily = useCallback(
    async (target: readonly StandoutMomentConstructor[]): Promise<boolean> => {
      if (!currentUserId || target.length === 0) return false
      const sharedAt = new Date().toISOString()
      const shareable = target.filter(
        (moment) => moment.category !== "secret" && moment.dontShare !== true
      )
      if (shareable.length === 0) return false
      try {
        await Promise.all(
          shareable.map((moment) =>
            Firebase.getInstance().updateDocumentOrThrow(
              DOC_TYPES.STANDOUT_MOMENTS,
              moment.id,
              {
                ...familyStateWrite(
                  "approved",
                  currentUserId,
                  sharedAt,
                  moment.sharedBy
                ),
                ...sentNoteSharePatch(moment),
              }
            )
          )
        )
        return true
      } catch {
        void message.error("Could not approve these for family. Try again.")
        return false
      }
    },
    [currentUserId, message]
  )

  const emptyText =
    moments.length === 0
      ? `No standout moments logged yet for ${formatSemester(filters.semester)}.`
      : "No moments match these filters."

  return (
    <div
      data-testid="standout-review-view"
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        background: `linear-gradient(to right, ${theme.body1}, ${theme.body2})`,
      }}
    >
      <Flex
        col
        gap={10}
        style={{ padding: "12px 20px 10px", flex: "0 0 auto" }}
      >
        <Flex gap={16} wrap style={{ alignItems: "center" }}>
          <Title level={4} style={{ margin: 0 }}>
            Standout Review
          </Title>
          <Select
            data-testid="sr-semester"
            aria-label="Semester"
            value={filters.semester}
            onChange={(value) => updateFilters({ semester: value })}
            style={{ width: 150 }}
          >
            {SemesterOptions({})}
          </Select>
          <Tooltip
            title={
              mode === REVIEW_MODE.REVIEW
                ? "The review is two columns for the last 3 months"
                : undefined
            }
          >
            <Select
              data-testid="sr-group-by"
              aria-label="Group by"
              value={filters.groupBy}
              onChange={(value) => updateFilters({ groupBy: value })}
              options={GROUP_BY_OPTIONS}
              style={{ width: 150 }}
              disabled={mode === REVIEW_MODE.REVIEW}
            />
          </Tooltip>
          {/* Wrapped in a span: antd group-typed components don't reliably
              forward data-* attributes (see test-environment skill). */}
          <span data-testid="sr-mode-toggle">
            <Segmented
              value={mode}
              onChange={(value) => setMode(value as ReviewMode)}
              options={[
                { label: "Review", value: REVIEW_MODE.REVIEW },
                { label: "Stream", value: REVIEW_MODE.STREAM },
                { label: "Teacher stats", value: REVIEW_MODE.TEACHER_STATS },
              ]}
            />
          </span>
        </Flex>

        <Flex gap={12} wrap style={{ alignItems: "center" }}>
          <Input
            data-testid="sr-search-student"
            aria-label="Search students"
            placeholder="Search students"
            allowClear
            prefix={<SearchOutlined />}
            value={studentSearch}
            onChange={(event) => setStudentSearch(event.target.value)}
            style={{ width: 200 }}
          />
          <Select
            data-testid="sr-filter-teacher"
            aria-label="Teacher"
            value={toSelectValue(filters.teacherId)}
            onChange={(value) =>
              updateFilters({ teacherId: fromSelectValue(value) })
            }
            options={teacherOptions}
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
          />
          <Select
            data-testid="sr-filter-class"
            aria-label="Class"
            value={toSelectValue(filters.classId)}
            onChange={(value) =>
              updateFilters({ classId: fromSelectValue(value) })
            }
            options={classOptions}
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
          />
          <Select
            data-testid="sr-filter-category"
            aria-label="Category"
            value={toSelectValue(filters.category)}
            onChange={(value) =>
              updateFilters({ category: fromSelectValue(value) })
            }
            options={categoryOptions}
            style={{ width: 200 }}
          />
          <Select
            data-testid="sr-filter-curriculum"
            aria-label="Curriculum"
            value={toSelectValue(filters.curriculumId)}
            onChange={(value) =>
              updateFilters({ curriculumId: fromSelectValue(value) })
            }
            options={curriculumOptions}
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
          />
          <Flex gap={8} style={{ alignItems: "center" }}>
            <Switch
              data-testid="sr-hide-finished"
              aria-label="Hide finished items"
              checked={filters.hideFinished}
              onChange={(checked) => updateFilters({ hideFinished: checked })}
            />
            <Text>Hide finished items</Text>
          </Flex>
          <Flex gap={8} style={{ alignItems: "center" }}>
            <Switch
              data-testid="sr-hide-finished-classes"
              aria-label="Hide finished classes"
              checked={filters.hideFinishedClasses}
              onChange={(checked) =>
                updateFilters({ hideFinishedClasses: checked })
              }
            />
            <Text>Hide finished classes</Text>
          </Flex>
          <Button
            icon={<ClearOutlined />}
            onClick={clearFilters}
            disabled={!hasActiveFilters(filters) && !studentSearch.trim()}
            data-testid="sr-clear-filters"
          >
            Clear
          </Button>
          {loading ? (
            <span data-testid="sr-counts-loading">
              <Spin size="small" />
            </span>
          ) : (
            <Space size={4} data-testid="sr-counts">
              <Text strong>{momentsForCounts.length}</Text>
              <Text>
                {momentsForCounts.length === 1 ? "moment" : "moments"} ·
              </Text>
              <Text strong>{countDistinctTeachers(momentsForCounts)}</Text>
              <Text>teachers ·</Text>
              <Text strong>{shareCounts.undecided}</Text>
              <Text>undecided ·</Text>
              <Text strong>{shareCounts.decided}</Text>
              <Text>decided</Text>
            </Space>
          )}
        </Flex>
      </Flex>

      <div
        style={{ flex: "1 1 auto", overflow: "auto", padding: "0 20px 20px" }}
      >
        {loading ? (
          <StandoutReviewSkeleton />
        ) : mode === REVIEW_MODE.TEACHER_STATS ? (
          <StandoutTeacherStats
            moments={moments}
            joins={joins}
            semesterClasses={semesterClasses}
            canSeeNeedsWorkReceived={canModerate}
          />
        ) : mode === REVIEW_MODE.STREAM ? (
          streamReady ? (
            <StandoutReviewStream
              moments={filteredMoments}
              storyTimes={storyTimesInView}
              classImages={classImagesInView}
              groupBy={filters.groupBy}
              joins={joins}
              emptyText={emptyText}
              onApproveForFamily={handleApproveForFamily}
              canSeeNeedsWork={canModerate}
              canApproveFamily={canModerate}
              notifyAtByKey={notifyAtByKey}
            />
          ) : (
            <Spin data-testid="sr-stream-loading" />
          )
        ) : (
          <StandoutReviewColumns
            moments={historyFilteredMoments}
            sentMoments={historyMoments}
            scans={historyScans}
            sessionMoments={sessionMoments}
            sessionScans={sessionArtwork}
            sessionStoryTimes={sessionStoryTimes}
            joins={joins}
            notifyAtByKey={notifyAtByKey}
            hideFinished={filters.hideFinished}
            hideFinishedClasses={filters.hideFinishedClasses}
          />
        )}
      </div>
    </div>
  )
}

export default StandoutReviewView
